import { useEffect, useRef, useState } from 'react';
import type { SlashCommandItem } from './slash-command-extension';

interface SlashCommandPaletteProps {
  items: SlashCommandItem[];
  onSelect: (item: SlashCommandItem) => void;
  onClose: () => void;
}

/**
 * Floating command picker shown when the user types "/" in the editor.
 * Filtering happens upstream (see slash-command-extension.ts, driven by
 * @tiptap/suggestion's query tracking) — this component only renders the
 * already-filtered `items` and handles selection.
 *
 * Keyboard events are captured at the window level with `capture: true` so
 * they intercept before the underlying TipTap editor sees them — without
 * capture, the editor would insert a newline on Enter before the palette
 * ever got a chance to handle it.
 */
export function SlashCommandPalette({ items, onSelect, onClose }: SlashCommandPaletteProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // Resets the highlighted item to the top whenever the filtered list changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: items.length is a change trigger, not a value used inside the effect
  useEffect(() => {
    setActiveIndex(0);
  }, [items.length]);

  // Keeps the highlighted item visible inside the palette when using arrow keys.
  // biome-ignore lint/correctness/useExhaustiveDependencies: activeIndex is a change trigger to scroll the newly active item into view
  useEffect(() => {
    const activeItem = listRef.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]');
    activeItem?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setActiveIndex((i) => (i + 1) % items.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setActiveIndex((i) => (i - 1 + items.length) % items.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        const item = items[activeIndex];
        if (item) onSelect(item);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [items, activeIndex, onSelect, onClose]);

  // Hide entirely instead of showing an empty list so the editor feels responsive when no match.
  if (items.length === 0) return null;

  return (
    <div
      className="slash-palette"
      role="listbox"
      aria-label="Types de blocs"
      tabIndex={-1}
      ref={listRef}
    >
      {items.map((item, i) => (
        <div
          key={item.label}
          role="option"
          tabIndex={-1}
          aria-selected={i === activeIndex}
          className={`slash-palette__item${i === activeIndex ? ' is-active' : ''}`}
          // onMouseDown instead of onClick: prevents the click from blurring the editor
          // (which would close the palette) before onSelect fires.
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(item);
          }}
          onMouseEnter={() => setActiveIndex(i)}
        >
          <span className="material-symbols-rounded slash-palette__icon">{item.icon}</span>
          <span className="slash-palette__text">
            <span className="slash-palette__label">{item.label}</span>
            <span className="slash-palette__desc">{item.description}</span>
          </span>
        </div>
      ))}
    </div>
  );
}
